/**
 * Skin for the audio-only Video.js preset (`vjs-audio`).
 *
 * Video.js ships a chrome designed around a video viewport: a large empty stage, a centered big
 * play button and a translucent control bar overlay. For audio that stage is dead space, so this
 * skin collapses the player into a single-row control strip and restyles every control.
 *
 * Colors come from CSS variables re-declared under `[data-theme='dark']`, the attribute
 * `AppThemeProvider` mirrors onto `<html>`, so the player follows the app light/dark mode.
 *
 * Selectors are prefixed with `.video-js.video-js-new.vjs-audio` on purpose: `VideoJsPlayer`
 * always adds `video-js-new`, and its shared `styles.css` targets that class with rules meant for
 * the video players. The extra class raises specificity so audio wins without `!important`.
 */

.video-js.video-js-new.vjs-audio {
  /* MUI `palette.grey[50]` — theme does not override the grey scale. */
  --vjs-audio-surface: #fafafa;
  --vjs-audio-border: rgba(0, 0, 0, 0.12);
  --vjs-audio-text: rgba(0, 0, 0, 0.87);
  --vjs-audio-text-muted: rgba(0, 0, 0, 0.6);
  --vjs-audio-accent: #1cc0fc;
  --vjs-audio-accent-hover: #03a6e2;
  --vjs-audio-accent-contrast: #ffffff;
  /* Play control mirrors PlaybackIconBtnV2 (MUI action.hover / primary). */
  --vjs-audio-play-idle-bg: rgba(0, 0, 0, 0.04);
  --vjs-audio-play-idle-hover-bg: rgba(0, 0, 0, 0.08);
  --vjs-audio-play-idle-color: rgba(0, 0, 0, 0.87);
  --vjs-audio-track: rgba(0, 0, 0, 0.1);
  --vjs-audio-buffered: rgba(0, 0, 0, 0.2);
  --vjs-audio-hover: rgba(0, 0, 0, 0.06);
  --vjs-audio-tooltip-bg: #000848;
  --vjs-audio-tooltip-text: #ffffff;
}

[data-theme='dark'] .video-js.video-js-new.vjs-audio {
  /* MUI dark `palette.background.paper`. */
  --vjs-audio-surface: #1a1d2e;
  --vjs-audio-border: #3a4560;
  --vjs-audio-text: #cdd6f4;
  --vjs-audio-text-muted: #8892b0;
  --vjs-audio-accent: #1cc0fc;
  --vjs-audio-accent-hover: #15a8db;
  --vjs-audio-accent-contrast: #fff;
  --vjs-audio-play-idle-bg: rgba(205, 214, 244, 0.08);
  --vjs-audio-play-idle-hover-bg: rgba(205, 214, 244, 0.16);
  --vjs-audio-play-idle-color: #cdd6f4;
  --vjs-audio-track: rgba(205, 214, 244, 0.14);
  --vjs-audio-buffered: rgba(205, 214, 244, 0.22);
  --vjs-audio-hover: rgba(205, 214, 244, 0.08);
  --vjs-audio-tooltip-bg: #0e1120;
  --vjs-audio-tooltip-text: #cdd6f4;
}

/* ---------------------------------------------------------------- container */

.video-js.video-js-new.vjs-audio {
  width: 100%;
  max-width: 100%;
  background-color: var(--vjs-audio-surface);
  border: 1px solid var(--vjs-audio-border);
  border-radius: 10px;
  color: var(--vjs-audio-text);
  font-family: var(--font-family-sans-serif);
}

/*
 * The <video> element has no visual role here, but it must stay in the layout: `display: none`
 * makes some browsers drop playback, so it is collapsed and made transparent instead.
 */
.video-js.video-js-new.vjs-audio .vjs-tech {
  height: 0;
  min-height: 0;
  opacity: 0;
  pointer-events: none;
}

.video-js.video-js-new.vjs-audio .vjs-poster,
.video-js.video-js-new.vjs-audio .vjs-big-play-button,
.video-js.video-js-new.vjs-audio .vjs-text-track-display,
.video-js.video-js-new.vjs-audio .vjs-custom-control-spacer,
.video-js.video-js-new.vjs-audio .vjs-remaining-time,
.video-js.video-js-new.vjs-audio .vjs-live-control,
.video-js.video-js-new.vjs-audio .vjs-seek-to-live-control,
.video-js.video-js-new.vjs-audio .vjs-fullscreen-control,
.video-js.video-js-new.vjs-audio .vjs-picture-in-picture-control,
.video-js.video-js-new.vjs-audio .vjs-subs-caps-button,
.video-js.video-js-new.vjs-audio .vjs-chapters-button,
.video-js.video-js-new.vjs-audio .vjs-descriptions-button,
.video-js.video-js-new.vjs-audio .vjs-audio-button {
  display: none !important;
}

/* --------------------------------------------------------------- control bar */

/*
 * The control bar becomes the player: it fills the strip and never auto-hides, since there is no
 * video underneath that hiding it could reveal.
 */
.video-js.video-js-new.vjs-audio .vjs-control-bar {
  position: absolute;
  inset: 0;
  display: flex !important;
  align-items: center;
  gap: 2px;
  height: 100%;
  padding: 0 12px;
  background: transparent;
  color: var(--vjs-audio-text);
  opacity: 1 !important;
  visibility: visible !important;
  transform: none !important;
}

.video-js.video-js-new.vjs-audio .vjs-control:focus,
.video-js.video-js-new.vjs-audio .vjs-control:focus-visible {
  outline: none;
  text-shadow: none;
}

.video-js.video-js-new.vjs-audio .vjs-control:focus-visible {
  box-shadow: 0 0 0 2px var(--vjs-audio-accent);
  border-radius: 6px;
}

/* -------------------------------------------------------------- play control */

/*
 * Sized and colored to mirror PlaybackIconBtnV2 (medium = 40px). Idle uses a soft fill;
 * playing (`.vjs-playing` on the button) switches to primary like the MUI control.
 */
.video-js.video-js-new.vjs-audio .vjs-play-control {
  order: 1;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  margin-right: 6px;
  border-radius: 50%;
  background-color: var(--vjs-audio-play-idle-bg);
  color: var(--vjs-audio-play-idle-color);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease;
}

.video-js.video-js-new.vjs-audio .vjs-play-control:hover,
.video-js.video-js-new.vjs-audio .vjs-play-control:focus {
  background-color: var(--vjs-audio-play-idle-hover-bg);
  color: var(--vjs-audio-play-idle-color);
}

.video-js.video-js-new.vjs-audio .vjs-play-control.vjs-playing {
  background-color: var(--vjs-audio-accent);
  color: var(--vjs-audio-accent-contrast);
}

.video-js.video-js-new.vjs-audio .vjs-play-control.vjs-playing:hover,
.video-js.video-js-new.vjs-audio .vjs-play-control.vjs-playing:focus {
  background-color: var(--vjs-audio-accent-hover);
  color: var(--vjs-audio-accent-contrast);
}

.video-js.video-js-new.vjs-audio .vjs-play-control:active {
  transform: scale(0.94);
}

.video-js.video-js-new.vjs-audio .vjs-play-control .vjs-icon-placeholder::before {
  position: static;
  font-size: 24px;
  line-height: 1;
}

/* ------------------------------------------------------------- time displays */

/*
 * Grouped as `current / duration` before the seek bar (podcast-style), instead of bookending
 * the progress control. Video.js already emits the slash via `.vjs-time-divider`.
 */
.video-js.video-js-new.vjs-audio .vjs-time-control {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  width: auto;
  min-width: 0;
  padding: 0;
  color: var(--vjs-audio-text-muted);
  font-size: 14px;
  line-height: 1;
  /* Keeps the strip from jittering as digits change width while playing. */
  font-variant-numeric: tabular-nums;
}

.video-js.video-js-new.vjs-audio .vjs-current-time {
  order: 2;
  padding-left: 4px;
}

.video-js.video-js-new.vjs-audio .vjs-time-divider {
  order: 3;
  padding: 0 4px;
}

.video-js.video-js-new.vjs-audio .vjs-duration {
  order: 4;
  padding-right: 4px;
}

/* ----------------------------------------------------------------- progress */

.video-js.video-js-new.vjs-audio .vjs-progress-control {
  order: 5;
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 40px;
  height: 100%;
  margin: 0 8px;
}

.video-js.video-js-new.vjs-audio .vjs-progress-holder {
  height: 6px;
  margin: 0;
  border-radius: 999px;
  background-color: var(--vjs-audio-track);
  transition: height 0.15s ease;
}

.video-js.video-js-new.vjs-audio .vjs-progress-control:hover .vjs-progress-holder {
  height: 8px;
}

.video-js.video-js-new.vjs-audio .vjs-load-progress {
  border-radius: 999px;
  background-color: var(--vjs-audio-buffered) !important;
}

/* Video.js paints per-range children inside the buffered bar; the flat bar above is enough. */
.video-js.video-js-new.vjs-audio .vjs-load-progress div {
  background: transparent !important;
}

.video-js.video-js-new.vjs-audio .vjs-play-progress {
  border-radius: 999px;
  background-color: var(--vjs-audio-accent) !important;
}

/* Replaces the Video.js icon-font handle with a plain dot pinned to the playhead. */
.video-js.video-js-new.vjs-audio .vjs-play-progress::before {
  content: '';
  display: block !important;
  position: absolute;
  top: 50%;
  right: -6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--vjs-audio-accent);
  box-shadow: 0 0 0 2px var(--vjs-audio-surface);
  transform: translateY(-50%);
}

/* Hide only the playhead's own tooltip; the seek-bar hover tooltip stays visible. */
.video-js.video-js-new.vjs-audio .vjs-play-progress .vjs-time-tooltip {
  display: none !important;
}

.video-js.video-js-new.vjs-audio .vjs-mouse-display {
  background-color: var(--vjs-audio-text-muted);
}

.video-js.video-js-new.vjs-audio .vjs-mouse-display .vjs-time-tooltip {
  top: -2.5em;
  padding: 3px 6px;
  border-radius: 4px;
  background-color: var(--vjs-audio-tooltip-bg);
  color: var(--vjs-audio-tooltip-text);
  font-family: inherit;
  font-size: 11px;
}

/* ------------------------------------------------------------ playback rate */

.video-js.video-js-new.vjs-audio .vjs-playback-rate {
  order: 6;
  flex: 0 0 auto;
  width: 40px;
  min-width: 40px;
}

/* Video.js absolutely positions the label over the full control height, so centering is done here. */
.video-js.video-js-new.vjs-audio .vjs-playback-rate .vjs-playback-rate-value {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
}

.video-js.video-js-new.vjs-audio .vjs-playback-rate .vjs-menu {
  /* Default Video.js gap is 1.5em; bump it so the list clears the 56px strip. */
  margin-bottom: 2.5em;
}

.video-js.video-js-new.vjs-audio .vjs-playback-rate .vjs-menu .vjs-menu-content {
  bottom: 2.5em;
  border-radius: 8px;
  background-color: var(--vjs-audio-tooltip-bg);
  color: var(--vjs-audio-tooltip-text);
  font-size: 12px;
}

/* Compact rows keep the popup short enough to fit above a 56px strip near the top of a screen. */
.video-js.video-js-new.vjs-audio .vjs-menu li {
  padding: 5px 10px;
  font-size: 12px;
  line-height: 1.4;
  text-transform: none;
}

.video-js.video-js-new.vjs-audio .vjs-menu li:hover,
.video-js.video-js-new.vjs-audio .vjs-menu li:focus {
  background-color: rgba(255, 255, 255, 0.12);
}

.video-js.video-js-new.vjs-audio .vjs-menu li.vjs-selected {
  background-color: var(--vjs-audio-accent);
  color: var(--vjs-audio-accent-contrast);
}

/* ------------------------------------------------------------------- volume */

/*
 * Kept even though `volumePanel` is disabled on VideoJsAudioPlayer, so re-enabling the control
 * does not require restyling from scratch.
 *
 * Video.js gives every `.vjs-control` `height: 100%`, so the control bar's `align-items: center`
 * cannot center them; the volume panel and its slider are centered from the inside instead.
 */
.video-js.video-js-new.vjs-audio .vjs-volume-panel {
  order: 7;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  width: auto;
  height: 100%;
  margin-left: 2px;
}

/*
 * The stock slider is collapsed to 1px and only expands on hover. On a strip this short there is
 * room to keep it out permanently, which also avoids a layout shift on every hover.
 */
.video-js.video-js-new.vjs-audio .vjs-volume-panel .vjs-volume-control {
  display: flex;
  align-items: center;
  position: relative;
  width: 56px;
  height: 100%;
  margin: 0;
  opacity: 1;
  visibility: visible;
  transition: none;
}

.video-js.video-js-new.vjs-audio .vjs-mute-control {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--vjs-audio-text-muted);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.video-js.video-js-new.vjs-audio .vjs-mute-control:hover {
  background-color: var(--vjs-audio-hover);
  color: var(--vjs-audio-text);
}

.video-js.video-js-new.vjs-audio .vjs-mute-control .vjs-icon-placeholder::before {
  position: static;
  font-size: 17px;
  line-height: 1;
}

.video-js.video-js-new.vjs-audio .vjs-volume-bar.vjs-slider-horizontal {
  flex: 1 1 auto;
  width: auto;
  height: 4px;
  margin: 0 8px 0 2px;
  border-radius: 999px;
  background-color: var(--vjs-audio-track);
}

.video-js.video-js-new.vjs-audio .vjs-volume-level {
  border-radius: 999px;
  background-color: var(--vjs-audio-accent);
}

/* Matches the seek handle: a plain dot instead of the Video.js icon-font circle. */
.video-js.video-js-new.vjs-audio .vjs-volume-level::before {
  content: '';
  position: absolute;
  top: 50%;
  right: -5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--vjs-audio-accent);
  transform: translateY(-50%);
}

/* ---------------------------------------------------- loading / error states */

/*
 * The stock spinner is 6em wide and would overflow the strip. Video.js sizes it entirely in `em`,
 * so shrinking the font-size scales the whole thing; it is then parked over the play button.
 */
.video-js.video-js-new.vjs-audio .vjs-loading-spinner {
  top: 50%;
  left: 32px;
  border-color: var(--vjs-audio-track);
  font-size: 3px;
}

.video-js.video-js-new.vjs-audio .vjs-loading-spinner::before,
.video-js.video-js-new.vjs-audio .vjs-loading-spinner::after {
  border-top-color: var(--vjs-audio-accent);
}

.video-js.video-js-new.vjs-audio .vjs-error-display .vjs-modal-dialog-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background-color: var(--vjs-audio-surface);
  color: var(--vjs-audio-text);
  font-size: 12px;
  text-align: center;
}

/* ---------------------------------------------------------------- size: large */

/*
 * Applied via `size="large"` on VideoJsAudioPlayer (`vjs-audio-large`). Scales the strip and
 * controls without changing the control order or theme tokens.
 */
.video-js.video-js-new.vjs-audio.vjs-audio-large {
  border-radius: 12px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-control-bar {
  padding: 0 16px;
  gap: 4px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-play-control {
  width: 48px;
  min-width: 48px;
  height: 48px;
  margin-right: 10px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-play-control .vjs-icon-placeholder::before {
  font-size: 28px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-time-control {
  font-size: 16px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-progress-control {
  margin: 0 12px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-progress-holder {
  height: 8px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-progress-control:hover .vjs-progress-holder {
  height: 10px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-play-progress::before {
  right: -7px;
  width: 14px;
  height: 14px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-mouse-display .vjs-time-tooltip {
  font-size: 12px;
  padding: 4px 8px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-playback-rate {
  width: 48px;
  min-width: 48px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-playback-rate .vjs-playback-rate-value {
  font-size: 14px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-playback-rate .vjs-menu {
  margin-bottom: 3em;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-playback-rate .vjs-menu .vjs-menu-content {
  bottom: 3em;
  font-size: 13px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-menu li {
  padding: 6px 12px;
  font-size: 13px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-mute-control {
  width: 40px;
  min-width: 40px;
  height: 40px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-mute-control .vjs-icon-placeholder::before {
  font-size: 20px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-volume-panel .vjs-volume-control {
  width: 72px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-volume-bar.vjs-slider-horizontal {
  height: 5px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-volume-level::before {
  right: -6px;
  width: 12px;
  height: 12px;
}

.video-js.video-js-new.vjs-audio.vjs-audio-large .vjs-loading-spinner {
  left: 40px;
  font-size: 4px;
}
